// =============================================================================
// main.js – Application orchestrator
// =============================================================================
// Initializes the app, wires up tabs, clock, service worker, and delegates
// state management to store.js.
// =============================================================================

import { CFG } from "../data/config.js";
import { LINE_CONFIG } from "./line-config.js";
import { loadLinesForCity, loadSingleLine, getCachedLineData } from "./line-registry.js";
import { renderLive, stopLiveTicker } from "./live.js";
import { renderTimetable } from "./timetable.js";
import { renderSettings } from "./settings.js";
import { getItalianDayName, getAppDate } from "./utils.js";
import { initMap } from "./map.js";
import { initTheme } from "./theme.js";
import {
  initNotifications,
  getNotificationConfig,
  saveNotificationConfig
} from "./notifications.js";
import { initFirebase, getCurrentUser, saveToCloud, onAuthStateChanged } from "./firebase-sync.js";
import { shouldShowOnboarding, startOnboarding } from "./onboarding.js";
import { initAlerts, renderAlertsTab, renderStrikeBanner, dismissAlert } from "./alerts.js";
import { showToast } from "./toast.js";
import {
  getState,
  getLineData,
  getLineConfig,
  setLineData,
  mergeLineData,
  getActiveConfig,
  saveSettings,
  subscribe,
  setSuppressCloudWrite
} from "./store";

// Re-export from store for backward compat (any external code using these)
export {
  getState,
  getLineData,
  getLineConfig,
  getActiveConfig,
  saveSettings,
  setSuppressCloudWrite
};

// ─── Global Error Handlers (REL-1) ──────────────────────────────────────────
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    console.error("[Trasporti Unhandled Error]", event.error || event.message);
    if (typeof showToast === "function") {
      showToast("Si è verificato un errore imprevisto.");
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    console.error("[Trasporti Unhandled Rejection]", event.reason);
    if (typeof showToast === "function") {
      showToast("Errore di rete o sincronizzazione.");
    }
  });
}


// ─── Lazy-loaded trains tab ─────────────────────────────────────────────────

let _renderTrainsTab = null;
async function loadTrainsTab() {
  if (!_renderTrainsTab) {
    try {
      const mod = await import("./trains-tab.js");
      _renderTrainsTab = mod.renderTrainsTab;
    } catch (e) {
      console.warn("[Trasporti] Tab Treni non disponibile:", e);
    }
  }
  return _renderTrainsTab;
}

let _renderJourneyTab = null;
async function loadJourneyTab() {
  if (!_renderJourneyTab) {
    try {
      const mod = await import("./journey-view.js");
      _renderJourneyTab = mod.renderJourneyTab;
    } catch (e) {
      console.warn("[Trasporti] Tab Viaggio non disponibile:", e);
    }
  }
  return _renderJourneyTab;
}

let _renderLiveMapTab = null;
async function loadLiveMapTab() {
  if (!_renderLiveMapTab) {
    try {
      const mod = await import("./live-map-view.js");
      _renderLiveMapTab = mod.renderLiveMapTab;
    } catch (e) {
      console.warn("[Trasporti] Tab Mappa Live non disponibile:", e);
    }
  }
  return _renderLiveMapTab;
}

let _renderLinesInfoTab = null;
async function loadLinesInfoTab() {
  if (!_renderLinesInfoTab) {
    try {
      const mod = await import("./lines-info-view.js");
      _renderLinesInfoTab = mod.renderLinesInfo;
    } catch (e) {
      console.warn("[Trasporti] Tab Linee Z non disponibile:", e);
    }
  }
  return _renderLinesInfoTab;
}

// ─── Line data loading ──────────────────────────────────────────────────────

let _staticFallbackLoaded = false;

async function loadStaticFallback() {
  if (_staticFallbackLoaded) return;
  _staticFallbackLoaded = true;
  try {
    const [z649, z627, z644, z625, z647, z642] = await Promise.all([
      import("../data/z649.js").then((m) => m.Z649_DATA),
      import("../data/z627.js").then((m) => m.Z627_DATA),
      import("../data/z644.js").then((m) => m.Z644_DATA),
      import("../data/z625.js").then((m) => m.Z625_DATA),
      import("../data/z647.js").then((m) => m.Z647_DATA),
      import("../data/z642.js").then((m) => m.Z642_DATA)
    ]);
    setLineData({ Z649: z649, Z627: z627, Z644: z644, Z625: z625, Z647: z647, Z642: z642 });
  } catch (e) {
    console.error("[Trasporti] Anche il fallback statico è fallito:", e);
  }
}

async function reloadLinesForCurrentCity() {
  const state = getState();
  const focusCity = state.settings?.focusCity || CFG.defaults?.focusCity || "BT";
  try {
    const loaded = await loadLinesForCity(CFG, focusCity);
    mergeLineData(loaded);
    state.linesLoaded = true;

    const activeCFG = getActiveConfig(state, CFG);
    const firstLine = activeCFG.lineOrder?.[0] || Object.keys(getLineData())[0] || "Z649";
    if (!state.timetableLine) state.timetableLine = firstLine;
    if (!state.settingsPanelLine) state.settingsPanelLine = firstLine;
  } catch (e) {
    console.error("[Trasporti] Errore nel caricamento linee:", e);
    if (Object.keys(getLineData()).length === 0) {
      await loadStaticFallback();
    }
    state.linesLoaded = true;
  }
}

// ─── Rendering ──────────────────────────────────────────────────────────────

let _rendering = false;

function renderCurrentTab() {
  if (_rendering) return;
  _rendering = true;

  const state = getState();
  const LINE_DATA = getLineData();

  const errorMessage = `<div class="empty-state" style="border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.08); color: #fecaca;">
    <strong>Errore di rendering</strong><br>
    <small>Si è verificato un problema nel caricamento di questa sezione. Prova a ricaricare la pagina.</small>
  </div>`;

  if (!state.linesLoaded && (state.currentTab === "live" || state.currentTab === "timetable" || state.currentTab === "journey" || state.currentTab === "live-map")) {
    const container = document.getElementById(`${state.currentTab}-content`);
    if (container) {
      container.innerHTML = `<div class="empty-state">Caricamento orari...</div>`;
    }
    _rendering = false;
    return;
  }

  try {
    const activeCFG = getActiveConfig(state, CFG);
    if (state.currentTab === "live")
      renderLive(state, LINE_DATA, LINE_CONFIG, activeCFG, saveSettings);
    else if (state.currentTab === "timetable")
      renderTimetable(state, LINE_DATA, LINE_CONFIG, activeCFG, saveSettings);
    else if (state.currentTab === "trains") {
      loadTrainsTab().then((fn) => {
        if (fn) fn(state, activeCFG, saveSettings);
        else {
          const c = document.getElementById("trains-content");
          if (c) c.innerHTML = `<div class="empty-state">Tab Treni in caricamento...</div>`;
        }
      });
    } else if (state.currentTab === "journey") {
      loadJourneyTab().then((fn) => {
        if (fn) fn(state, activeCFG, saveSettings, LINE_DATA, LINE_CONFIG);
        else {
          const c = document.getElementById("journey-content");
          if (c) c.innerHTML = `<div class="empty-state">Tab Viaggio in caricamento...</div>`;
        }
      });
    } else if (state.currentTab === "live-map") {
      loadLiveMapTab().then((fn) => {
        if (fn) fn(state, LINE_DATA, LINE_CONFIG, activeCFG, saveSettings);
        else {
          const c = document.getElementById("live-map-content");
          if (c) c.innerHTML = `<div class="empty-state">Tab Mappa Live in caricamento...</div>`;
        }
      });
    } else if (state.currentTab === "alerts") renderAlertsTab(state);
    else if (state.currentTab === "lines") {
      loadLinesInfoTab().then((fn) => {
        if (fn) fn(document.getElementById("lines-content"));
        else {
          const c = document.getElementById("lines-content");
          if (c) c.innerHTML = `<div class="empty-state">Tab Linee Z in caricamento...</div>`;
        }
      });
    }
    else if (state.currentTab === "settings")
      renderSettings(state, saveSettings, activeCFG, LINE_DATA, LINE_CONFIG);
  } catch (error) {
    console.error(`[Trasporti] Errore nel render del tab "${state.currentTab}":`, error);
    const container = document.getElementById(`${state.currentTab}-content`);
    if (container) container.innerHTML = errorMessage;
  } finally {
    _rendering = false;
  }
}

// Subscribe the renderer to state changes (from saveSettings)
subscribe(() => {
  renderCurrentTab();
});

// Listen for city-change events that require line reloading
document.addEventListener("trasporti:state-changed", (e) => {
  if (e.detail?.cityChanged) {
    reloadLinesForCurrentCity().then(() => renderCurrentTab());
  }
});

// ─── Tab switching ──────────────────────────────────────────────────────────

function switchTab(tabId) {
  const state = getState();
  state.currentTab = tabId;
  if (tabId !== "live") {
    stopLiveTicker();
  }
  if (tabId !== "live-map" && window._liveMapLayer) {
    window._liveMapLayer.pause();
  }

  // Toggle body class for fullscreen live-map layout
  document.body.classList.toggle("live-map-active-body", tabId === "live-map");

  document.querySelectorAll(".tab-btn").forEach((btn) => {
    const isActive = btn.dataset.tab === tabId;
    btn.classList.toggle("active", isActive);
    btn.setAttribute("aria-selected", isActive ? "true" : "false");
  });
  // P6: .view is the Figma-aligned alias for legacy .tab-panel. Toggle both
  // so renames don't regress legacy consumers (js/*.js may still add to either).
  document.querySelectorAll(".tab-panel, .view").forEach((panel) => {
    panel.classList.toggle("active", panel.id === `tab-${tabId}`);
  });
  renderCurrentTab();
}

// ─── "Altro" overflow sheet (extras: LIVE / Vai a / Impostazioni) ───────────
// Phase 1 of feature/figma-alignment. The sheet buttons carry .tab-btn +
// data-tab so the existing switchTab() click binding handles them; we only
// wire open/close and pillitate the background.

function setupAltroSheet() {
  const sheet = document.getElementById("altro-sheet");
  const trigger = document.getElementById("nav-altro");
  const backdrop = document.getElementById("altro-backdrop");
  const closeBtn = document.getElementById("altro-close");
  if (!sheet || !trigger) return;

  const open = (e) => {
    e.preventDefault();
    sheet.classList.add("open");
    sheet.setAttribute("aria-hidden", "false");
    trigger.setAttribute("aria-expanded", "true");
  };
  const close = () => {
    sheet.classList.remove("open");
    sheet.setAttribute("aria-hidden", "true");
    trigger.setAttribute("aria-expanded", "false");
  };

  trigger.addEventListener("click", open);
  if (backdrop) backdrop.addEventListener("click", close);
  if (closeBtn) closeBtn.addEventListener("click", close);
  // Close on Esc
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && sheet.classList.contains("open")) close();
  });
  // Close after a tab switch (sheet items delegate to switchTab via .tab-btn binding)
  sheet.addEventListener("click", (e) => {
    if (e.target.closest(".altro-item")) {
      // Allow switchTab to fire first, then collapse sheet.
      setTimeout(close, 0);
    }
  });
}

// ─── Clock ──────────────────────────────────────────────────────────────────

function updateClock() {
  const now = getAppDate();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  // Figma-aligned live-clock: HH:MM:SS with mono seconds dimmed.
  const clk = document.getElementById("clk");
  if (clk) clk.innerHTML = `${hh}:${mm}<span class="sec">:${ss}</span>`;
  // Legacy clock-card still has spans if present (kept for back-compat).
  const tEl = document.getElementById("clock-time");
  if (tEl) tEl.textContent = `${hh}:${mm}:${ss}`;
  const dEl = document.getElementById("clock-date");
  if (dEl) dEl.textContent = `${getItalianDayName(now)} ${now.toLocaleDateString("it-IT")}`;
  // Figma brand-date: "domenica 19 luglio" (Italian day + day-of-month + month-name).
  const bd = document.getElementById("brand-date");
  if (bd) {
    const dayName = getItalianDayName(now).toLowerCase();
    const dayNum = now.getDate();
    const monthName = now.toLocaleDateString("it-IT", { month: "long" });
    bd.textContent = `${dayName} ${dayNum} ${monthName}`;
  }
}

window.updateClock = updateClock;

// ─── Service Worker ─────────────────────────────────────────────────────────

function registerSW() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker
    .register("./sw.js")
    .then((reg) => {
      reg.update().catch(() => {});
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "activated" && navigator.serviceWorker.controller) {
            window.location.reload();
          }
        });
      });
    })
    .catch((error) => {
      console.warn("Service worker non registrato.", error);
    });
}

// ─── Init ───────────────────────────────────────────────────────────────────

async function init() {
  const state = getState();
  window._app_config = { CFG, switchTab, renderCurrentTab };
  document.querySelectorAll(".tab-btn").forEach((btn) => {
    btn.addEventListener("click", () => switchTab(btn.dataset.tab));
  });
  setupAltroSheet();
  // Global click delegate for elements requesting tab switches (e.g. alert warning badges)
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-switch-to-tab]");
    if (trigger) {
      switchTab(trigger.dataset.switchToTab);
    }
  });
  updateClock();
  setInterval(updateClock, 1000);

  // Sync pill & connectivity
  const updateSyncPill = (forcedStatus) => {
    const pill = document.getElementById("sync-pill");
    const text = document.getElementById("sync-status-text");
    if (!pill || !text) return;

    pill.className = "sync-pill";
    if (!navigator.onLine) {
      pill.classList.add("sync-status-offline");
      text.textContent = "Offline - Dati Locali";
      pill.title = "Sei disconnesso dalla rete. L'app usa i dati locali salvati sul dispositivo.";
      return;
    }

    const user = getCurrentUser();
    if (!user) {
      pill.classList.add("sync-status-synced");
      pill.style.background = "rgba(100, 116, 139, 0.12)";
      pill.style.color = "var(--muted)";
      pill.style.borderColor = "var(--line)";
      text.textContent = "Dati Locali";
      pill.title = "Sei online. Accedi nelle Impostazioni per attivare la sincronizzazione cloud.";
      return;
    }

    pill.style.background = "";
    pill.style.color = "";
    pill.style.borderColor = "";

    const status =
      typeof forcedStatus === "string" ? forcedStatus : window._last_sync_status || "synced";
    window._last_sync_status = status;

    if (status === "pending" || status === "syncing") {
      pill.classList.add("sync-status-syncing");
      text.textContent = "Sincronizzazione...";
      pill.title = "Sincronizzazione in corso con il cloud...";
    } else if (status === "error") {
      pill.classList.add("sync-status-offline");
      text.textContent = "Errore Sync";
      pill.title =
        "Si è verificato un errore durante il salvataggio sul cloud o connettività offline.";
    } else if (status === "locked" || status === "suspended") {
      pill.classList.add("sync-status-offline");
      pill.style.background = "rgba(239, 68, 68, 0.12)";
      pill.style.color = "#fca5a5";
      pill.style.borderColor = "rgba(239, 68, 68, 0.4)";
      text.textContent = "Sync Bloccato 🔒";
      pill.title =
        "La sincronizzazione cloud è bloccata. Inserisci la passphrase in Impostazioni per sbloccare.";
    } else {
      pill.classList.add("sync-status-synced");
      text.textContent = "Sincronizzato";
      pill.title = `Sincronizzato con il cloud (${user.email}).`;
    }
  };

  window.addEventListener("online", updateSyncPill);
  window.addEventListener("offline", updateSyncPill);
  window.addEventListener("trasporti:sync-status", (e) => updateSyncPill(e.detail.status));

  // Keyboard navigation for unified responsive tab bar
  const tabBar = document.querySelector(".nav-bottom [role='tablist']");
  if (tabBar) {
    tabBar.addEventListener("keydown", (e) => {
      const tabs = Array.from(tabBar.querySelectorAll("[role='tab']")).filter(
        (t) => window.getComputedStyle(t).display !== "none"
      );
      let index = tabs.findIndex((t) => t.classList.contains("active"));
      if (index === -1) return;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        index = (index + 1) % tabs.length;
        tabs[index].focus();
        switchTab(tabs[index].dataset.tab);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        index = (index - 1 + tabs.length) % tabs.length;
        tabs[index].focus();
        switchTab(tabs[index].dataset.tab);
      } else if (e.key === "Home") {
        e.preventDefault();
        tabs[0].focus();
        switchTab(tabs[0].dataset.tab);
      } else if (e.key === "End") {
        e.preventDefault();
        tabs[tabs.length - 1].focus();
        switchTab(tabs[tabs.length - 1].dataset.tab);
      }
    });
  }

  try {
    initFirebase();
  } catch (e) {
    console.warn("[Firebase]", e);
  }
  try {
    updateSyncPill();
  } catch (e) {
    console.error("[Trasporti] Errore aggiornamento pillola sync:", e);
  }

  if (state.settings?.largeText) {
    document.body.classList.add("accessibility-large-text");
  } else {
    document.body.classList.remove("accessibility-large-text");
  }

  subscribe(() => {
    const s = getState();
    if (s.settings?.largeText) {
      document.body.classList.add("accessibility-large-text");
    } else {
      document.body.classList.remove("accessibility-large-text");
    }
  });

  onAuthStateChanged(() => {
    updateSyncPill();
    if (state.currentTab === "settings") renderCurrentTab();
  });
  document.addEventListener("trasporti:settings-changed", () => {
    updateSyncPill();
    if (state.currentTab === "settings") renderCurrentTab();
  });

  // Load line data
  try {
    await reloadLinesForCurrentCity();
  } catch (e) {
    console.error("[Trasporti] Errore critico nel caricamento linee:", e);
    state.linesLoaded = true;
  }

  // Onboarding
  if (shouldShowOnboarding(state.settings)) {
    startOnboarding((profile) => {
      if (profile && !profile.skipped) {
        const updates = { userProfile: profile };
        if (profile.walkMinutes) updates.walkRossini = profile.walkMinutes;
        if (profile.stationReachMinutes) {
          updates.stationReachMinutes = {
            ...state.settings.stationReachMinutes,
            ...profile.stationReachMinutes
          };
        }
        if (profile.driveCanegrate) {
          updates.driveCanegrate = profile.driveCanegrate;
          if (!updates.stationReachMinutes) {
            updates.stationReachMinutes = { ...state.settings.stationReachMinutes };
          }
          if (updates.stationReachMinutes.CN_FS === undefined || updates.stationReachMinutes.CN_FS === 0) {
            updates.stationReachMinutes.CN_FS = profile.driveCanegrate;
          }
        }
        if (profile.focusCity) updates.focusCity = profile.focusCity;
        if (profile.visibleTrains) updates.visibleTrains = profile.visibleTrains;
        if (profile.liveHero) updates.liveHero = profile.liveHero;
        if (profile.favoriteStops && Object.keys(profile.favoriteStops).length > 0) {
          updates.favoriteStops = { ...state.settings.favoriteStops, ...profile.favoriteStops };
        }
        saveSettings(updates);
        if (profile.wantNotifications && profile.activeLines?.length > 0) {
          import("./notifications.js").then(
            ({ requestPermission, getNotificationConfig, saveNotificationConfig }) => {
              requestPermission().then((perm) => {
                if (perm === "granted") {
                  const notifConfig = getNotificationConfig();
                  notifConfig.enabled = true;
                  notifConfig.followedLines = [
                    ...new Set([...notifConfig.followedLines, ...profile.activeLines])
                  ];
                  if (profile.useCanegrate) notifConfig.followedLines.push("canegrate");
                  saveNotificationConfig(notifConfig);
                }
              });
            }
          );
        }
      } else if (profile) {
        saveSettings({ userProfile: profile });
      }
      // Default tab is LIVE (NAV-DEFAULT: LIVE shows real-time countdown immediately).
      switchTab("live");
    });
  } else {
    // Default tab is LIVE (NAV-DEFAULT: LIVE shows real-time countdown immediately).
    switchTab("live");
  }

  // Background line loading
  if (typeof process === "undefined" || process.env?.NODE_ENV !== "test") {
    setTimeout(ensureAllLinesLoaded, 1000);
  }

  // Auto-refresh every 60s
  setInterval(() => {
    if (state.currentTab === "live" || state.currentTab === "timetable") {
      // V3: don't yank the re-render if the user has UI open — the patchDOM
      // re-render would close the stop-sheet modal and collapse expanded
      // trip cards mid-interaction.
      const selectOpen = document.querySelector(
        ".dep-stop-select[style*='display: inline'], .dep-stop-select[style*='display:inline']"
      );
      const anyVisible =
        selectOpen ||
        [...document.querySelectorAll(".dep-stop-select")].some(
          (s) => s.style.display && s.style.display !== "none"
        );
      if (anyVisible) return;
      if (document.querySelector("#stop-sheet-modal.open")) return;
      if (document.querySelector(".trip-card.expanded")) return;
      renderCurrentTab();
    }
  }, 60000);

  try {
    initMap();
  } catch (e) {
    console.warn("[Trasporti] Mappa non inizializzata:", e);
  }
  initTheme();
  initAlerts(() => {
    renderCurrentTab();
  });
  initNotifications(
    () => getState(),
    () => getLineData(),
    () => LINE_CONFIG,
    () => getActiveConfig(getState(), CFG)
  );
  // §6: wire the live-clock chip as the entry-point for the day+hour
  // simulator. Module self-registers on #clk; safe to call before the
  // first renderCurrentTab() because the chip lives in index.html.
  try {
    const { initTimeSimTrigger } = await import("./time-sim.js");
    initTimeSimTrigger();
  } catch (e) {
    console.warn("[Trasporti] Time-sim trigger non inizializzato:", e);
  }
  registerSW();
}

document.addEventListener("DOMContentLoaded", () => {
  init().catch((e) => {
    console.error("[Trasporti] Errore fatale nell'inizializzazione:", e);
    const liveContent = document.getElementById("live-content");
    if (liveContent) {
      liveContent.innerHTML = `<div class="empty-state" style="border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.08); color: #fecaca;">
        <strong>Errore di avvio</strong><br>
        <small>${e.message || "Errore sconosciuto"}. Prova a ricaricare la pagina (Ctrl+Shift+R).</small>
      </div>`;
    }
  });
});

// ─── Public API for on-demand line loading ──────────────────────────────────

export async function ensureLineLoaded(lineId) {
  const LINE_DATA = getLineData();
  if (LINE_DATA[lineId]) return LINE_DATA[lineId];
  const data = await loadSingleLine(lineId);
  if (data) {
    mergeLineData({ [lineId]: data });
  }
  return data;
}

export async function ensureAllLinesLoaded() {
  try {
    const { getAllLineIds, loadLines, getCachedLineData } = await import("./line-registry.js");
    const allIds = getAllLineIds();
    const loaded = await loadLines(allIds);
    mergeLineData(loaded);
  } catch (e) {
    console.warn("[Trasporti] Impossibile caricare tutte le linee in background:", e);
  }
  return getLineData();
}
